import React, { useEffect, useState } from 'react';
import { Plus, X, Trash2, Ban, Bell, Check, Edit3 } from 'lucide-react';
import { bookingSlotsApi, bookingsApi, clientsApi, remindersApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { SkeletonTable } from '../components/Skeleton';
import './Pages.css';

interface Slot {
  id: string;
  start_time: string;
  end_time: string;
  slot_type: string;
  is_available: boolean;
  location_type: string;
  capacity: number;
}

interface Booking {
  id: string;
  booking_reference: string;
  booking_slot_id: string | null;
  client_id: string | null;
  status: string;
  notes: string | null;
  cancellation_reason: string | null;
  confirmed_at: string | null;
  cancelled_at: string | null;
  guest_name: string | null;
  guest_email: string | null;
  guest_message: string | null;
  reminder_sent: boolean;
  created_at: string;
}

interface Client {
  id: string;
  first_name: string;
  last_name: string;
}

const statusLabels: Record<string, string> = {
  confirmed: 'Bestätigt',
  pending: 'Ausstehend',
  cancelled: 'Storniert',
  completed: 'Abgeschlossen',
  no_show: 'Nicht erschienen',
};

const locationLabels: Record<string, string> = {
  in_person: 'Vor Ort',
  online: 'Online',
  both: 'Beides',
};

export default function Bookings() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'slots' | 'bookings'>('bookings');
  const [slots, setSlots] = useState<Slot[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSlotForm, setShowSlotForm] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [sendingReminders, setSendingReminders] = useState(false);

  const loadSlots = async () => {
    const res = await bookingSlotsApi.list();
    setSlots(res.data.items);
  };

  const loadBookings = async () => {
    const res = await bookingsApi.list();
    setBookings(res.data.items);
  };

  useEffect(() => {
    Promise.all([loadSlots(), loadBookings()]).finally(() => setLoading(false));
  }, []);

  const deleteSlot = async (id: string) => {
    try {
      await bookingSlotsApi.delete(id);
      loadSlots();
      showToast('Slot gelöscht', 'info');
    } catch { showToast('Fehler beim Löschen', 'error'); }
  };

  const cancelBooking = async (id: string) => {
    try {
      await bookingsApi.update(id, { status: 'cancelled', cancellation_reason: 'Vom Coach storniert' });
      loadBookings();
      loadSlots();
      showToast('Buchung storniert', 'info');
    } catch { showToast('Fehler beim Stornieren', 'error'); }
  };

  const confirmBooking = async (id: string) => {
    try {
      await bookingsApi.update(id, { status: 'confirmed' });
      loadBookings();
      showToast('Buchung bestätigt', 'success');
    } catch { showToast('Fehler beim Bestätigen', 'error'); }
  };

  const fmtDateTime = (iso: string) =>
    new Date(iso).toLocaleString('de-DE', {
      weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      <div className="flex-between mb-24">
        <div>
          <h1>Buchungssystem</h1>
          <p className="text-muted text-sm">Verfügbarkeiten und Buchungen verwalten</p>
        </div>
        <div className="flex gap-8">
          <button
            className="btn btn-secondary"
            onClick={async () => {
              setSendingReminders(true);
              try {
                const res = await remindersApi.checkAppointments();
                const { checked, sent, errors } = res.data;
                if (sent > 0) {
                  showToast(`${sent} Erinnerung(en) versendet (${checked} Termine geprüft).`, 'success');
                } else {
                  showToast(`Keine Erinnerungen zu versenden (${checked} Termine geprüft).`, 'info');
                }
              } catch (err: any) {
                showToast(err.response?.data?.detail || 'Fehler beim Versenden der Erinnerungen', 'error');
              } finally {
                setSendingReminders(false);
              }
            }}
            disabled={sendingReminders}
          >
            <Bell size={16} /> {sendingReminders ? 'Sende...' : 'Erinnerungen senden'}
          </button>
          <button className="btn btn-secondary" onClick={() => setShowSlotForm(true)}>
            <Plus size={18} /> Slot hinzufügen
          </button>
          <button className="btn btn-primary" onClick={() => setShowBookingForm(true)}>
            <Plus size={18} /> Neue Buchung
          </button>
        </div>
      </div>

      <div className="category-chips mb-24">
        <button className={`chip ${activeTab === 'slots' ? 'chip-active' : ''}`} onClick={() => setActiveTab('slots')}>
          Verfügbarkeiten ({slots.length})
        </button>
        <button className={`chip ${activeTab === 'bookings' ? 'chip-active' : ''}`} onClick={() => setActiveTab('bookings')}>
          Buchungen ({bookings.length})
        </button>
      </div>

      {loading && <SkeletonTable rows={5} />}

      {activeTab === 'slots' && !loading && (
        <>
          {slots.length === 0 ? (
            <div className="empty-state card">
              <p>Noch keine Verfügbarkeiten eingetragen.</p>
              <button className="btn btn-primary mt-16" onClick={() => setShowSlotForm(true)}>
                <Plus size={18} /> Verfügbarkeit eintragen
              </button>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Datum / Zeit</th>
                    <th>Ende</th>
                    <th>Ort</th>
                    <th>Status</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.map((s) => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 500 }}>{fmtDateTime(s.start_time)}</td>
                      <td className="text-light">{fmtTime(s.end_time)}</td>
                      <td className="text-light">{locationLabels[s.location_type] || s.location_type}</td>
                      <td>
                        <span className={`badge ${s.is_available ? 'badge-active' : 'badge-completed'}`}>
                          {s.is_available ? 'Frei' : 'Gebucht'}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-danger" onClick={() => deleteSlot(s.id)} title="Löschen">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'bookings' && !loading && (
        <>
          {bookings.length === 0 ? (
            <div className="empty-state card">
              <p>Noch keine Buchungen vorhanden.</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Referenz</th>
                    <th>Klient / Gast</th>
                    <th>Erstellt</th>
                    <th>Notizen</th>
                    <th>Status</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} onClick={() => setSelectedBooking(b)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>{b.booking_reference}</td>
                      <td>
                        {b.guest_name ? (
                          <div>
                            <div style={{ fontWeight: 500 }}>{b.guest_name}</div>
                            <div className="text-muted" style={{ fontSize: '0.78rem' }}>{b.guest_email}</div>
                          </div>
                        ) : (
                          <span className="text-light">—</span>
                        )}
                      </td>
                      <td className="text-light">{fmtDateTime(b.created_at)}</td>
                      <td className="text-light">{b.guest_message || b.notes || '—'}</td>
                      <td>
                        <span className={`badge ${b.status === 'confirmed' ? 'badge-active' : b.status === 'cancelled' ? 'badge-paused' : 'badge-new'}`}>
                          {statusLabels[b.status] || b.status}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-4" onClick={(e) => e.stopPropagation()}>
                          {b.status === 'pending' && (
                            <button className="btn btn-sm btn-primary" onClick={() => confirmBooking(b.id)} title="Bestätigen">
                              <Check size={14} />
                            </button>
                          )}
                          {(b.status === 'confirmed' || b.status === 'pending') && (
                            <button className="btn btn-sm btn-danger" onClick={() => cancelBooking(b.id)} title="Stornieren">
                              <Ban size={14} />
                            </button>
                          )}
                          <button className="btn btn-sm btn-secondary" onClick={() => setSelectedBooking(b)} title="Bearbeiten">
                            <Edit3 size={14} /> Öffnen
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showSlotForm && <SlotForm onClose={() => setShowSlotForm(false)} onSaved={loadSlots} />}
      {showBookingForm && (
        <BookingForm
          slots={slots.filter((s) => s.is_available)}
          onClose={() => setShowBookingForm(false)}
          onSaved={() => { loadBookings(); loadSlots(); }}
        />
      )}
      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          slots={slots}
          onClose={() => setSelectedBooking(null)}
          onSaved={() => { loadBookings(); loadSlots(); }}
        />
      )}
    </div>
  );
}

function SlotForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { showToast } = useToast();
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [locationType, setLocationType] = useState('both');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await bookingSlotsApi.create({
        start_time: `${date}T${startTime}:00`,
        end_time: `${date}T${endTime}:00`,
        location_type: locationType,
      });
      onSaved();
      onClose();
      showToast('Slot erfolgreich erstellt', 'success');
    } catch { showToast('Fehler beim Erstellen des Slots', 'error'); } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Verfügbarkeit eintragen</h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Datum *</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Von *</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Bis *</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
            </div>
          </div>
          <div className="form-group">
            <label>Ort</label>
            <select value={locationType} onChange={(e) => setLocationType(e.target.value)}>
              <option value="both">Vor Ort oder Online</option>
              <option value="in_person">Nur Vor Ort</option>
              <option value="online">Nur Online</option>
            </select>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Speichern...' : 'Slot anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BookingForm({ slots, onClose, onSaved }: { slots: Slot[]; onClose: () => void; onSaved: () => void }) {
  const { showToast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [slotId, setSlotId] = useState('');
  const [clientId, setClientId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    clientsApi.list({ limit: 200 }).then((res) => setClients(res.data.items));
  }, []);

  const fmtSlot = (s: Slot) => {
    const d = new Date(s.start_time);
    const end = new Date(s.end_time);
    return `${d.toLocaleDateString('de-DE')} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await bookingsApi.create({
        booking_slot_id: slotId || undefined,
        client_id: clientId || undefined,
        notes: notes || undefined,
      });
      onSaved();
      onClose();
    } catch { showToast('Fehler beim Erstellen der Buchung', 'error'); } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Neue Buchung</h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Verfügbarer Slot</label>
            <select value={slotId} onChange={(e) => setSlotId(e.target.value)}>
              <option value="">— Slot wählen —</option>
              {slots.map((s) => (
                <option key={s.id} value={s.id}>{fmtSlot(s)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Klient</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">— Kein Klient —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Notizen</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Speichern...' : 'Buchung erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BookingDetailModal({
  booking,
  slots,
  onClose,
  onSaved,
}: {
  booking: Booking;
  slots: Slot[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { showToast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [status, setStatus] = useState(booking.status);
  const [notes, setNotes] = useState(booking.notes || '');
  const [guestName, setGuestName] = useState(booking.guest_name || '');
  const [guestEmail, setGuestEmail] = useState(booking.guest_email || '');
  const [guestMessage, setGuestMessage] = useState(booking.guest_message || '');
  const [clientId, setClientId] = useState(booking.client_id || '');
  const [slotId, setSlotId] = useState(booking.booking_slot_id || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    clientsApi.list({ limit: 200 }).then((res) => setClients(res.data.items));
  }, []);

  const currentSlot = slots.find((s) => s.id === booking.booking_slot_id);

  const fmtSlot = (s: Slot) => {
    const d = new Date(s.start_time);
    const end = new Date(s.end_time);
    return `${d.toLocaleDateString('de-DE')} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const fmtDateTime = (iso: string) =>
    new Date(iso).toLocaleString('de-DE', {
      weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      await bookingsApi.update(booking.id, {
        status,
        notes: notes || null,
        guest_name: guestName || null,
        guest_email: guestEmail || null,
        guest_message: guestMessage || null,
        client_id: clientId || null,
        booking_slot_id: slotId || null,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Speichern', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Available slots = free ones + the currently assigned slot
  const availableSlots = slots.filter((s) => s.is_available || s.id === booking.booking_slot_id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>Buchung {booking.booking_reference}</h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        <div style={{ padding: '0 24px' }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <span className="text-muted" style={{ fontSize: '0.82rem' }}>
              Erstellt: {fmtDateTime(booking.created_at)}
            </span>
            {booking.confirmed_at && (
              <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                Bestätigt: {fmtDateTime(booking.confirmed_at)}
              </span>
            )}
            {booking.cancelled_at && (
              <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                Storniert: {fmtDateTime(booking.cancelled_at)}
              </span>
            )}
            {booking.reminder_sent && (
              <span className="badge badge-completed" style={{ fontSize: '0.75rem' }}>Erinnerung gesendet</span>
            )}
          </div>
        </div>

        <div style={{ padding: '0 24px 16px' }}>
          <div className="form-group">
            <label>Termin-Slot</label>
            <select value={slotId} onChange={(e) => setSlotId(e.target.value)}>
              <option value="">— Kein Slot —</option>
              {availableSlots.map((s) => (
                <option key={s.id} value={s.id}>{fmtSlot(s)}</option>
              ))}
            </select>
            {currentSlot && (
              <span className="text-muted" style={{ fontSize: '0.78rem', marginTop: 4, display: 'block' }}>
                {locationLabels[currentSlot.location_type] || currentSlot.location_type}
              </span>
            )}
          </div>

          <div className="form-group">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Klient</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">— Kein Klient —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
              ))}
            </select>
          </div>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
            <p className="text-muted text-sm" style={{ marginBottom: 8 }}>Gast-Informationen (bei Buchung ohne Konto)</p>
            <div className="form-row">
              <div className="form-group">
                <label>Gast-Name</label>
                <input type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Vor- und Nachname" />
              </div>
              <div className="form-group">
                <label>Gast-E-Mail</label>
                <input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="email@beispiel.de" />
              </div>
            </div>
            <div className="form-group">
              <label>Nachricht vom Gast</label>
              <textarea value={guestMessage} onChange={(e) => setGuestMessage(e.target.value)} rows={2} placeholder="Nachricht des Gastes..." />
            </div>
          </div>

          <div className="form-group">
            <label>Notizen</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Interne Notizen zur Buchung..." />
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichern...' : 'Änderungen speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
